import axios from 'axios';
import { getTripAccess, clearTripAccess } from './tripAccess';
import { getRoundUnlockToken, setRoundUnlockToken, getScoreEditUnlockToken, setScoreEditUnlockToken, clearLegacyOwnerTokens, getGroupClaimToken, getGroupClaimData, getDeviceId } from './ownerAuth';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Clean up legacy tokens on module load
clearLegacyOwnerTokens();

// Parse trip code and round ID from round URLs (including /strokes sub-path)
function _parseRoundUrl(url) {
  const m = url.match(/^\/trips\/([A-Z0-9]+)\/rounds\/([^/]+)/i);
  return m ? { tripCode: m[1].toUpperCase(), roundId: m[2] } : null;
}

// Check if URL is specifically the /strokes endpoint
function _isStrokesUrl(url) {
  return /^\/trips\/[A-Z0-9]+\/rounds\/[^/]+\/strokes$/i.test(url);
}

api.interceptors.request.use((config) => {
  // Attach per-trip access header for /trips/{code}/* requests
  const url = config.url || '';
  const match = url.match(/^\/trips\/([A-Z0-9]+)/i);
  if (match && !url.endsWith('/verify-access') && !url.endsWith('/meta')) {
    const code = match[1].toUpperCase();
    const access = getTripAccess(code);
    if (access?.password) {
      config.headers['X-Trip-Access'] = access.password;
    }
    // Send group claim tokens on GET /trips for freeze scrubbing
    if (config.method === 'get' && /^\/trips\/[A-Z0-9]+$/i.test(url)) {
      try {
        const prefix = `foretour:groupClaim:${code}:`;
        const claims = {};
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(prefix)) {
            const roundId = key.slice(prefix.length);
            const raw = localStorage.getItem(key);
            if (raw) {
              const data = JSON.parse(raw);
              if (data?.token) claims[roundId] = data.token;
            }
          }
        }
        if (Object.keys(claims).length > 0) {
          config.headers['X-Group-Claims'] = JSON.stringify(claims);
        }
      } catch {}
    }
  }
  const parsed = _parseRoundUrl(url);
  if (parsed && (config.method === 'patch' || config.method === 'delete')) {
    // Context C: Attach score-edit unlock token on /strokes PATCH
    if (_isStrokesUrl(url) && config.method === 'patch') {
      const scoreToken = getScoreEditUnlockToken(parsed.tripCode, parsed.roundId);
      if (scoreToken) {
        config.headers['X-Owner-Access'] = scoreToken;
      }
      const claimToken = getGroupClaimToken(parsed.tripCode, parsed.roundId);
      if (claimToken) {
        config.headers['X-Group-Claim'] = claimToken;
      }
      config.headers['X-Device-Id'] = getDeviceId();
    }
    // Context B: Attach per-round structural unlock token on round mutations (not /strokes)
    if (!_isStrokesUrl(url)) {
      const roundToken = getRoundUnlockToken(parsed.tripCode, parsed.roundId);
      if (roundToken) {
        config.headers['X-Owner-Access'] = roundToken;
      }
    }
  }
  // Auto-attach platform token for organiser/settings/pricing/owner/approve-capacity routes
  if (/^\/(organiser|settings|pricing|owner)(\/|$)/i.test(url) || /\/approve-capacity$/i.test(url)) {
    const platformToken = sessionStorage.getItem('foretour_platform_token');
    if (platformToken && !config.headers['X-Owner-Access']) {
      config.headers['X-Owner-Access'] = platformToken;
    }
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // Handle invalid trip access
    if (err.response?.status === 403 && err.response?.data?.code === 'invalid_trip_access') {
      const url = err?.config?.url || '';
      const m = url.match(/^\/trips\/([A-Z0-9]+)/i);
      if (m) {
        clearTripAccess(m[1].toUpperCase());
        window.dispatchEvent(new CustomEvent('trip-access-invalid', { detail: { code: m[1].toUpperCase() } }));
      }
    }
    // Handle owner_required (trip creation) — surface to caller
    if (err.response?.status === 403 && err.response?.data?.detail === 'owner_required') {
      // Just let it propagate — CreateEvent handles it inline
    }
    // Handle owner_required_for_scored_round, owner_required_for_revealed_round, or trip_owner_password_not_set
    const ownerDetail = err.response?.data?.detail;
    if (err.response?.status === 403 && (ownerDetail === 'owner_required_for_scored_round' || ownerDetail === 'owner_required_for_revealed_round' || ownerDetail === 'trip_owner_password_not_set')) {
      const reqUrl = err?.config?.url || '';
      const parsed = _parseRoundUrl(reqUrl);
      const tripMatch = reqUrl.match(/^\/trips\/([A-Z0-9]+)$/i);
      const isScoreEdit = ownerDetail === 'owner_required_for_revealed_round';
      const isNotSet = ownerDetail === 'trip_owner_password_not_set';
      return new Promise((resolve, reject) => {
        const onUnlocked = (e) => {
          window.removeEventListener('foretour:owner-unlocked', onUnlocked);
          window.removeEventListener('foretour:owner-cancelled', onCancelled);
          const token = e.detail?.token;
          if (parsed && token) {
            if (isScoreEdit) {
              setScoreEditUnlockToken(parsed.tripCode, parsed.roundId, token);
            } else {
              setRoundUnlockToken(parsed.tripCode, parsed.roundId, token);
            }
          }
          const retryConfig = { ...err.config, headers: { ...err.config.headers, 'X-Owner-Access': token } };
          resolve(api(retryConfig));
        };
        const onCancelled = () => {
          window.removeEventListener('foretour:owner-unlocked', onUnlocked);
          window.removeEventListener('foretour:owner-cancelled', onCancelled);
          reject(err);
        };
        window.addEventListener('foretour:owner-unlocked', onUnlocked, { once: true });
        window.addEventListener('foretour:owner-cancelled', onCancelled, { once: true });
        window.dispatchEvent(new CustomEvent('foretour:owner-required', {
          detail: {
            tripCode: parsed?.tripCode || (tripMatch ? tripMatch[1].toUpperCase() : null),
            roundId: parsed?.roundId || null,
            reason: isScoreEdit ? 'revealed_round' : isNotSet ? 'not_set' : 'scored_round',
          },
        }));
      });
    }
    return Promise.reject(err);
  }
);

export const ping = () => api.get('/ping');
export const getCourses = () => api.get('/courses');
export const getTrip = (code, etag) => api.get(`/trips/${code}`, {
  headers: etag ? { 'If-None-Match': etag } : {},
  validateStatus: (s) => s === 200 || s === 304,
});
export const getTripMeta = (code) => api.get(`/trips/${code}/meta`);
export const verifyTripAccess = (code, password) => api.post(`/trips/${code}/verify-access`, { password });
export const createTrip = (data, ownerToken) => {
  const headers = {};
  if (ownerToken) headers['X-Owner-Access'] = ownerToken;
  return api.post('/trips', data, { headers });
};
export const updateTrip = (code, data) => api.put(`/trips/${code}`, data);
export const patchTrip = (code, data) => api.patch(`/trips/${code}`, data);
export const deleteTrip = (code, body) => api.delete(`/trips/${code}`, { data: body });
export const addRound = (code, data) => api.post(`/trips/${code}/rounds`, data);
export const patchRound = (code, roundId, data) => api.patch(`/trips/${code}/rounds/${roundId}`, data);
export const deleteRound = (code, roundId, body) => api.delete(`/trips/${code}/rounds/${roundId}`, { data: body });
export const patchStrokes = (code, roundId, data) => api.patch(`/trips/${code}/rounds/${roundId}/strokes`, data);
export const getRoundStrokes = (code, roundId) => api.get(`/trips/${code}/rounds/${roundId}/strokes`);
export const patchPlayerActive = (code, playerId, data) => api.patch(`/trips/${code}/players/${playerId}/active`, data);
export const approveCapacity = (code, data) => api.post(`/trips/${code}/approve-capacity`, data);
export const claimGroup = (code, roundId, data) => api.post(`/trips/${code}/rounds/${roundId}/claim-group`, data);
export const releaseGroup = (code, roundId, data) => api.post(`/trips/${code}/rounds/${roundId}/release-group`, data);
export const getClaimMeta = (code, roundId, groupIdx) => api.get(`/trips/${code}/rounds/${roundId}/groups/${groupIdx}/claim`);
export const takeoverClaim = (code, roundId, groupIdx, data) => api.post(`/trips/${code}/rounds/${roundId}/groups/${groupIdx}/claim/takeover`, data);
export const deleteClaim = (code, roundId, groupIdx) => api.delete(`/trips/${code}/rounds/${roundId}/groups/${groupIdx}/claim`);
export const rotateUserPin = (code, data) => api.post(`/trips/${code}/rotate-user-pin`, data);
export const bulkAddPlayer = (code, data) => api.post(`/trips/${code}/players/bulk-add`, data);

export default api;
